import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import {
  SelectedClient,
  SelectedProject,
  WorkflowStepId,
  WORKFLOW_STEPS,
  projectColor,
} from '../types/projectWorkflow';
import { Project } from '../types';
import { buildAiExploreSitesUrl, isProjectAiSetupEnabled } from '../utils/aiSuggestionsEngine';

const EMPTY_DONE_STEPS: Record<WorkflowStepId, boolean> = {
  overview: false,
  sites: false,
  plan: false,
  order: false,
  cart: false,
  status: false,
  reporting: false,
};

interface ProjectWorkflowContextValue {
  selectedClient: SelectedClient | null;
  selectedProject: SelectedProject | null;
  activeStep: WorkflowStepId | null;
  doneSteps: Record<WorkflowStepId, boolean>;
  panelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  togglePanel: () => void;
  selectProject: (
    project: Project,
    clientName: string,
    colorIndex?: number,
    initialStep?: WorkflowStepId,
    options?: { from?: 'projects' },
  ) => void;
  clearProject: () => void;
  workflowEntryPoint: 'projects' | null;
  goToStep: (step: WorkflowStepId, options?: { fresh?: boolean }) => void;
  markStepDone: (step: WorkflowStepId) => void;
  /** Clear pipeline greens (after checkout or starting a new order). */
  resetDoneSteps: () => void;
  isGlobalView: boolean;
  navTarget: string;
  setNavTarget: (target: string) => void;
  refreshProjectsKey: number;
  bumpProjectsRefresh: () => void;
  refreshClientsKey: number;
  bumpClientsRefresh: () => void;
  panelFocusClientId: string | null;
  focusClientInPanel: (clientId: string) => void;
  clearPanelFocusClient: () => void;
  /** Bumped when user clicks Home — panel opens and top project → Explore Sites */
  homeLaunchKey: number;
  requestHomeLaunch: () => void;
  patchClientInSelection: (clientId: string, name: string) => void;
  patchProjectInSelection: (projectId: string, projectName: string, clientName?: string) => void;
}

const ProjectWorkflowContext = createContext<ProjectWorkflowContextValue | undefined>(
  undefined
);

function stepFromPath(pathname: string): WorkflowStepId | null {
  // AI setup wizard is project-scoped but not a workflow tab.
  if (pathname.includes('/project-workflow/ai-setup')) return null;
  if (pathname.includes('/project-workflow/overview')) return 'overview';
  if (pathname.includes('/project-workflow/reporting')) return 'reporting';
  if (pathname.startsWith('/sites')) return 'sites';
  // Legacy Create Order URL — treat as Place Order (route redirects there too).
  if (pathname.includes('/project-workflow/plan')) return 'order';
  if (pathname.startsWith('/planning')) return 'plan';
  if (pathname.includes('/project-workflow/place-order')) return 'order';
  if (pathname.includes('/project-workflow/cart')) return 'cart';
  if (pathname.startsWith('/guest-blog-placements')) return 'status';
  if (pathname.startsWith('/profile')) return null;
  return null;
}

function buildProjectQuery(projectId: string, clientId: string, planId?: string | null): string {
  let q = `projectId=${encodeURIComponent(projectId)}&clientId=${encodeURIComponent(clientId)}`;
  if (planId) q += `&planId=${encodeURIComponent(planId)}`;
  return q;
}

function pathForStep(
  step: WorkflowStepId,
  projectId: string,
  clientId: string,
  planId?: string | null
): string {
  const q = buildProjectQuery(projectId, clientId, planId);
  switch (step) {
    case 'overview':
      return `/project-workflow/overview?${q}`;
    case 'sites': {
      if (isProjectAiSetupEnabled(projectId)) {
        const aiUrl = buildAiExploreSitesUrl(projectId, clientId);
        if (aiUrl) {
          if (planId) {
            const u = new URL(aiUrl, 'http://local');
            u.searchParams.set('planId', planId);
            return `/sites?${u.searchParams.toString()}`;
          }
          return aiUrl;
        }
      }
      return `/sites?${q}`;
    }
    case 'plan':
      return `/project-workflow/plan?${q}`;
    case 'order':
      return `/project-workflow/place-order?${q}`;
    case 'cart':
      return `/project-workflow/cart?${q}`;
    case 'status':
      return `/guest-blog-placements?${q}`;
    case 'reporting':
      return `/project-workflow/reporting?${q}`;
    default:
      return `/project-workflow/overview?${q}`;
  }
}

// Remember the last workflow step a user visited per project, so re-entering a
// project resumes where they left off. First-time projects default to Explore Sites.
const LAST_STEP_PREFIX = 'wf-last-step:';

function readLastStep(projectId: string): WorkflowStepId | null {
  try {
    const v = localStorage.getItem(`${LAST_STEP_PREFIX}${projectId}`);
    // Legacy Create Order step is skipped — resume at Place Order instead.
    if (v === 'plan') return 'order';
    if (v && WORKFLOW_STEPS.some((s) => s.id === v)) return v as WorkflowStepId;
  } catch {
    /* localStorage unavailable */
  }
  return null;
}

function writeLastStep(projectId: string, step: WorkflowStepId): void {
  try {
    localStorage.setItem(`${LAST_STEP_PREFIX}${projectId}`, step);
  } catch {
    /* localStorage unavailable */
  }
}

export const ProjectWorkflowProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isClient, isSuperAdmin } = useAuth();

  const [selectedClient, setSelectedClient] = useState<SelectedClient | null>(null);
  const [selectedProject, setSelectedProject] = useState<SelectedProject | null>(null);
  const [doneSteps, setDoneSteps] = useState<Record<WorkflowStepId, boolean>>({
    ...EMPTY_DONE_STEPS,
  });
  const [panelOpen, setPanelOpen] = useState(false);
  const [navTarget, setNavTarget] = useState('home');
  const [homeLaunchKey, setHomeLaunchKey] = useState(0);
  const [refreshProjectsKey, setRefreshProjectsKey] = useState(0);
  const [refreshClientsKey, setRefreshClientsKey] = useState(0);
  const [panelFocusClientId, setPanelFocusClientId] = useState<string | null>(null);
  const [workflowEntryPoint, setWorkflowEntryPoint] = useState<'projects' | null>(null);

  const urlProjectId =
    searchParams.get('standalone') === '1' ? null : searchParams.get('projectId');
  const urlClientId =
    searchParams.get('standalone') === '1' ? null : searchParams.get('clientId');

  const activeStep = useMemo(() => stepFromPath(location.pathname), [location.pathname]);

  const isGlobalView = !selectedProject && !urlProjectId;

  const bumpProjectsRefresh = useCallback(() => {
    setRefreshProjectsKey((k) => k + 1);
  }, []);

  const bumpClientsRefresh = useCallback(() => {
    setRefreshClientsKey((k) => k + 1);
  }, []);

  const focusClientInPanel = useCallback((clientId: string) => {
    setPanelFocusClientId(clientId);
    setPanelOpen(true);
  }, []);

  const clearPanelFocusClient = useCallback(() => {
    setPanelFocusClientId(null);
  }, []);

  const requestHomeLaunch = useCallback(() => {
    // Home: open Clients & Projects and launch top project → Explore Sites
    setPanelOpen(true);
    setHomeLaunchKey((k) => k + 1);
  }, []);

  const goToStep = useCallback(
    (step: WorkflowStepId, options?: { fresh?: boolean }) => {
      if (!selectedProject) return;
      // Create Order is removed from the workflow — send callers to Place Order.
      const resolvedStep: WorkflowStepId = step === 'plan' ? 'order' : step;
      // Keep the active plan id in the URL so Explore Sites ↔ Place Order
      // stays bound to the same saved plan when one exists.
      // Pass { fresh: true } to drop planId (e.g. "Explore more sites" from
      // Order Status should start a brand-new selection, not resume a saved order).
      const planId = options?.fresh ? null : searchParams.get('planId');
      if (options?.fresh) {
        setDoneSteps({ ...EMPTY_DONE_STEPS });
      }
      navigate(pathForStep(resolvedStep, selectedProject.id, selectedProject.clientId, planId));
    },
    [navigate, selectedProject, searchParams]
  );

  const selectProject = useCallback(
    (
      project: Project,
      clientName: string,
      colorIndex = 0,
      initialStep?: WorkflowStepId,
      options?: { from?: 'projects' },
    ) => {
      const clientId = project.clientId || project.client?.id || '';
      setSelectedClient({ id: clientId, name: clientName });
      setSelectedProject({
        id: project.id,
        projectName: project.projectName,
        clientId,
        clientName,
        color: projectColor(colorIndex),
      });
      setWorkflowEntryPoint(options?.from === 'projects' ? 'projects' : null);
      setDoneSteps({ ...EMPTY_DONE_STEPS });
      setPanelOpen(true);
      if (clientId) {
        setPanelFocusClientId(clientId);
      }
      // Resume the last step the user was on for this project; first-time
      // projects: Super Admin → Order Status, everyone else → Explore Sites.
      const defaultStep: WorkflowStepId = isSuperAdmin ? 'status' : 'sites';
      const resumeStep = initialStep ?? readLastStep(project.id) ?? defaultStep;
      navigate(pathForStep(resumeStep, project.id, clientId));
    },
    [navigate, isSuperAdmin]
  );

  const clearProject = useCallback(() => {
    setSelectedProject(null);
    setSelectedClient(null);
    setWorkflowEntryPoint(null);
  }, []);

  const patchClientInSelection = useCallback((clientId: string, name: string) => {
    setSelectedClient((prev) => (prev?.id === clientId ? { ...prev, name } : prev));
    setSelectedProject((prev) =>
      prev?.clientId === clientId ? { ...prev, clientName: name } : prev
    );
  }, []);

  const patchProjectInSelection = useCallback(
    (projectId: string, projectName: string, clientName?: string) => {
      setSelectedProject((prev) => {
        if (prev?.id !== projectId) return prev;
        return {
          ...prev,
          projectName,
          ...(clientName ? { clientName } : {}),
        };
      });
    },
    []
  );

  const markStepDone = useCallback((step: WorkflowStepId) => {
    setDoneSteps((prev) => ({ ...prev, [step]: true }));
  }, []);

  const resetDoneSteps = useCallback(() => {
    setDoneSteps({ ...EMPTY_DONE_STEPS });
  }, []);

  const togglePanel = useCallback(() => {
    setPanelOpen((p) => !p);
  }, []);

  // Persist the last visited step per project so re-entry resumes there.
  // Key off urlProjectId (NOT selectedProject.id): both activeStep and
  // urlProjectId derive from the URL, so they stay consistent. Using
  // selectedProject.id can lag a render behind the URL during a project switch
  // and write the new step under the *previous* project's id (corrupting it).
  useEffect(() => {
    if (urlProjectId && activeStep) {
      writeLastStep(urlProjectId, activeStep);
    }
  }, [activeStep, urlProjectId]);

  // Sync selection from URL when projectId present (deep links)
  useEffect(() => {
    if (!urlProjectId) return;
    if (selectedProject?.id === urlProjectId) return;
    // Minimal hydration — full project name filled when panel loads project list
    setSelectedProject((prev) =>
      prev?.id === urlProjectId
        ? prev
        : {
            id: urlProjectId,
            projectName: prev?.projectName || 'Project',
            clientId: urlClientId || prev?.clientId || '',
            clientName: prev?.clientName || '',
            color: prev?.color || projectColor(0),
          }
    );
    if (urlClientId) {
      setSelectedClient((prev) =>
        prev?.id === urlClientId ? prev : { id: urlClientId, name: prev?.name || 'Client' }
      );
    }
  }, [urlProjectId, urlClientId, selectedProject?.id]);

  // Dashboard is global — never keep a project highlighted here (sidebar click,
  // Home→Dashboard, or browser back from a project URL).
  useEffect(() => {
    if (location.pathname !== '/dashboard') return;
    if (urlProjectId) return;
    setSelectedProject(null);
    setSelectedClient(null);
    setWorkflowEntryPoint(null);
  }, [location.pathname, urlProjectId]);

  // Global Orders / notification deep-links (standalone=1): leave project workflow
  // so the Clients & Projects sidebar stays closed and no project stays selected.
  useEffect(() => {
    if (!location.pathname.startsWith('/guest-blog-placements')) return;
    if (searchParams.get('standalone') !== '1') return;
    setSelectedProject(null);
    setSelectedClient(null);
    setWorkflowEntryPoint(null);
    setPanelOpen(false);
  }, [location.pathname, searchParams, setPanelOpen]);

  const value = useMemo(
    () => ({
      selectedClient,
      selectedProject,
      activeStep,
      doneSteps,
      panelOpen,
      setPanelOpen,
      togglePanel,
      selectProject,
      clearProject,
      workflowEntryPoint,
      goToStep,
      markStepDone,
      resetDoneSteps,
      isGlobalView,
      navTarget,
      setNavTarget,
      refreshProjectsKey,
      bumpProjectsRefresh,
      refreshClientsKey,
      bumpClientsRefresh,
      panelFocusClientId,
      focusClientInPanel,
      clearPanelFocusClient,
      homeLaunchKey,
      requestHomeLaunch,
      patchClientInSelection,
      patchProjectInSelection,
    }),
    [
      selectedClient,
      selectedProject,
      activeStep,
      doneSteps,
      panelOpen,
      togglePanel,
      selectProject,
      clearProject,
      workflowEntryPoint,
      goToStep,
      markStepDone,
      resetDoneSteps,
      isGlobalView,
      navTarget,
      refreshProjectsKey,
      bumpProjectsRefresh,
      refreshClientsKey,
      bumpClientsRefresh,
      panelFocusClientId,
      focusClientInPanel,
      clearPanelFocusClient,
      homeLaunchKey,
      requestHomeLaunch,
      patchClientInSelection,
      patchProjectInSelection,
    ]
  );

  return (
    <ProjectWorkflowContext.Provider value={value}>
      {children}
    </ProjectWorkflowContext.Provider>
  );
};

export function useProjectWorkflow(): ProjectWorkflowContextValue {
  const ctx = useContext(ProjectWorkflowContext);
  if (!ctx) {
    throw new Error('useProjectWorkflow must be used within ProjectWorkflowProvider');
  }
  return ctx;
}

export function useProjectWorkflowOptional(): ProjectWorkflowContextValue | undefined {
  return useContext(ProjectWorkflowContext);
}

export { WORKFLOW_STEPS };
